/**
 * Barra de progreso para la cuota mensual de mensajes del Asistente IA.
 *
 * Sprint 18 hotfix (mayo 20, 2026): reemplaza el "X mensajes restantes
 * este mes" / "X / Y mensajes" que generaba ansiedad en el usuario
 * por una barra muda. El detalle exacto queda en el `title` (tooltip
 * en hover) para quien quiera consultarlo — progressive disclosure.
 *
 * Diseño:
 * - Track gris claro (`bg-slate-200`), redondeado.
 * - Fill que crece de izquierda a derecha conforme se consume la cuota.
 * - Color del fill cambia según el % usado:
 *   - <70%: emerald (verde) — todo bien
 *   - 70-90%: amber (ámbar) — empezar a moderar
 *   - >90%: red — casi agotada
 *   - 100% (agotada): red con label "agotada"
 * - Transición suave 300ms en width y color cuando cambia.
 * - Tooltip nativo con números exactos + fecha de renovación si está
 *   agotada.
 *
 * Accesibilidad:
 * - `role="progressbar"` con `aria-valuemin`, `aria-valuemax`,
 *   `aria-valuenow` y `aria-label` para lectores de pantalla.
 */

import type { QuotaUsageDTO } from "@/lib/api";

export interface QuotaProgressBarProps {
  quota: QuotaUsageDTO;
  /**
   * Clase Tailwind para el ancho del track (ej. "w-32", "w-full").
   * El componente NO impone un ancho — el caller decide. Default
   * "w-full" para que rellene su contenedor.
   */
  widthClass?: string;
  /**
   * Si true, muestra una etiqueta "Cuota agotada" debajo de la barra
   * cuando is_exhausted. Default true. Pasar false en contextos donde
   * el estado de agotamiento ya se comunica de otra forma (ej. cuando
   * el caller renderiza un LockedPanel completo).
   */
  showExhaustedLabel?: boolean;
}

function formatResetDate(iso: string | null): string {
  if (!iso) return "fin de mes";
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "fin de mes";
  }
}

export function QuotaProgressBar({
  quota,
  widthClass = "w-full",
  showExhaustedLabel = true,
}: QuotaProgressBarProps) {
  const limit = Math.max(quota.limit, 1); // evitar div/0
  const used = Math.min(Math.max(quota.used, 0), limit);
  const pctUsed = Math.round((used / limit) * 100);

  // Color del fill según severidad. Mismo color para track sutil.
  let fillClass = "bg-emerald-500";
  if (pctUsed >= 100) {
    fillClass = "bg-red-500";
  } else if (pctUsed >= 90) {
    fillClass = "bg-red-500";
  } else if (pctUsed >= 70) {
    fillClass = "bg-amber-500";
  }

  // Texto del tooltip — el dato preciso queda accesible bajo demanda.
  const tooltipParts = [`${used} de ${limit} mensajes usados este mes`];
  if (quota.is_exhausted && quota.period_resets_at) {
    tooltipParts.push(`Renueva el ${formatResetDate(quota.period_resets_at)}`);
  }
  const tooltip = tooltipParts.join(" · ");

  return (
    <div className={`flex flex-col gap-1 ${widthClass}`}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={used}
        aria-label={tooltip}
        title={tooltip}
        data-testid="quota-progress-bar"
        data-pct={pctUsed}
        className={`h-1.5 ${widthClass} cursor-help overflow-hidden rounded-full bg-slate-200`}
      >
        <div
          className={`h-full rounded-full transition-all duration-300 ease-out ${fillClass}`}
          style={{ width: `${pctUsed}%` }}
        />
      </div>
      {showExhaustedLabel && quota.is_exhausted && (
        <p data-testid="quota-exhausted-label" className="text-[10px] font-semibold text-red-700">
          Cuota agotada — renueva el {formatResetDate(quota.period_resets_at)}
        </p>
      )}
    </div>
  );
}
